import { useState, type ReactNode } from 'react';
import { CaretDownIcon } from '@phosphor-icons/react';
import {
  Button as AriaButton,
  Disclosure as AriaDisclosure,
  DisclosurePanel as AriaDisclosurePanel,
  Link as AriaLink,
} from 'react-aria-components';
import { cn } from './lib/cn';
import { quietScrollbar } from './lib/scrollbar';
import { formatCount } from './lib/format-count';

type SideNavEntryBase = {
  id: string;
  label: ReactNode;
  /** Sized by the caller; 16px lines up with the caret on a group. */
  icon?: ReactNode;
  count?: number;
};

export type SideNavItem = SideNavEntryBase & {
  href: string;
};

export type SideNavGroup = SideNavEntryBase & {
  items: SideNavItem[];
};

export type SideNavEntry = SideNavItem | SideNavGroup;

export type SideNavProps = {
  entries: SideNavEntry[];
  /** `id` of the entry the current route resolved to; a group's own `id`
   * marks the section without lighting one of its rows. */
  activeKey?: string;
  /** Where this navigation leads, for screen readers. */
  label: string;
  /** Brand, search, workspace — whatever sits above the list. */
  header?: ReactNode;
  /** Pinned below the list, behind a rule. */
  footer?: ReactNode;
  maxCount?: number;
  className?: string;
};

function isGroup(entry: SideNavEntry): entry is SideNavGroup {
  return 'items' in entry;
}

/*
 * Rows are one shape whether they navigate or open a group, so the two stay
 * on the same grid and the caret is the only thing that tells them apart.
 */
const ROW = cn(
  'sk:flex sk:w-full sk:items-center sk:gap-2 sk:rounded-option sk:px-2 sk:py-1.5',
  'sk:text-dense sk:font-medium sk:tracking-dense sk:text-fg-secondary sk:no-underline',
  'sk:transition-colors sk:duration-100',
  'sk:hover:bg-fill-tertiary sk:hover:text-fg',
  'sk:outline-none sk:rac-focus-visible:outline-2 sk:rac-focus-visible:outline-solid sk:rac-focus-visible:outline-offset-1 sk:rac-focus-visible:outline-primary',
);

/* Preflight is out of the stylesheet, so a button keeps its UA chrome. */
const BUTTON_RESET =
  'sk:m-0 sk:cursor-pointer sk:appearance-none sk:border-0 sk:bg-transparent sk:text-left sk:font-sans';

const LIST_RESET = 'sk:m-0 sk:flex sk:list-none sk:flex-col sk:gap-0.5 sk:p-0';

/* A step darker than hover: the column sits on the app's grey frame. */
const ACTIVE_ROW = 'sk:bg-fill-secondary sk:text-fg';

/* The section the reader is inside, said quietly: the row is not itself the
 * destination, so it takes the full foreground colour but not the fill the
 * leaf `ACTIVE_ROW` sits on. */
const ACTIVE_SECTION = 'sk:text-fg';

/**
 * A sidebar row's look, for links the column shows outside `entries` (its
 * footer, say), so they can't drift from the rows `SideNav` draws itself.
 */
export function sideNavRowStyles(isActive = false) {
  return cn(ROW, isActive && ACTIVE_ROW);
}

function EntryIcon({ icon }: { icon: ReactNode }) {
  return (
    <span className="sk:flex sk:size-4.5 sk:shrink-0 sk:items-center sk:justify-center">
      {icon}
    </span>
  );
}

function EntryCount({
  entry,
  maxCount,
}: {
  entry: SideNavEntryBase;
  maxCount: number | undefined;
}) {
  if (entry.count === undefined) {
    return null;
  }

  return (
    <span className="sk:inline-flex sk:h-4.5 sk:min-w-4.5 sk:shrink-0 sk:items-center sk:justify-center sk:rounded-full sk:bg-fill-secondary sk:px-1.5 sk:text-xs sk:font-semibold sk:tabular-nums sk:text-fg-secondary">
      {formatCount(entry.count, maxCount)}
    </span>
  );
}

function ItemRow({
  item,
  isActive,
  maxCount,
  isNested,
}: {
  item: SideNavItem;
  isActive: boolean;
  maxCount: number | undefined;
  isNested: boolean;
}) {
  return (
    <li>
      <AriaLink
        href={item.href}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          ROW,
          /* Without an icon a child indents until its label stands under its
             parent's; with one, the icon already spans most of that, so it
             steps in by a single unit and keeps the level legible. */
          isNested && (item.icon ? 'sk:pl-4' : 'sk:pl-8'),
          isActive && ACTIVE_ROW,
        )}
      >
        {item.icon ? <EntryIcon icon={item.icon} /> : null}
        <span className="sk:flex-1 sk:truncate">{item.label}</span>
        <EntryCount entry={item} maxCount={maxCount} />
      </AriaLink>
    </li>
  );
}

function GroupRow({
  group,
  activeKey,
  maxCount,
}: {
  group: SideNavGroup;
  activeKey: string | undefined;
  maxCount: number | undefined;
}) {
  /* A group's own id marks the section when the route belongs to it but to
     none of its children, so the section reads as open without a row lit. */
  const holdsActive =
    group.id === activeKey || group.items.some(item => item.id === activeKey);
  const [toggled, setToggled] = useState<boolean>();

  /*
   * The route decides which group stands open until someone says otherwise,
   * and their choice then holds while they stay on that route.
   */
  const isExpanded = toggled ?? holdsActive;

  return (
    <li>
      <AriaDisclosure
        isExpanded={isExpanded}
        onExpandedChange={setToggled}
        /* `data-expanded` lands here, so the caret reads it off the group. */
        className="sk:group"
      >
        <AriaButton
          slot="trigger"
          className={cn(ROW, BUTTON_RESET, holdsActive && ACTIVE_SECTION)}
        >
          {group.icon ? <EntryIcon icon={group.icon} /> : null}
          <span className="sk:flex-1 sk:truncate">{group.label}</span>
          <EntryCount entry={group} maxCount={maxCount} />
          <CaretDownIcon
            size={12}
            weight="bold"
            className="sk:shrink-0 sk:text-fg-tertiary sk:transition-transform sk:duration-150 sk:group-rac-expanded:rotate-180"
          />
        </AriaButton>
        <AriaDisclosurePanel className="sk:pt-0.5">
          <ul className={LIST_RESET}>
            {group.items.map(item => (
              <ItemRow
                key={item.id}
                item={item}
                isActive={item.id === activeKey}
                maxCount={maxCount}
                isNested
              />
            ))}
          </ul>
        </AriaDisclosurePanel>
      </AriaDisclosure>
    </li>
  );
}

/**
 * The app's standing navigation: one column of places, some of them grouped.
 * Only the list scrolls, so the header and footer stay put however long it
 * runs. It has no surface of its own: it lies flat on the app's frame, and the
 * page beside it is the card.
 */
export function SideNav({
  entries,
  activeKey,
  label,
  header,
  footer,
  maxCount = 99,
  className,
}: SideNavProps) {
  return (
    <nav
      aria-label={label}
      className={cn(
        'sk:group/nav sk:flex sk:h-full sk:w-54 sk:flex-col sk:overflow-hidden',
        className,
      )}
    >
      {header ? <div className="sk:shrink-0 sk:p-2">{header}</div> : null}
      <ul
        className={cn(
          LIST_RESET,
          'sk:min-h-0 sk:flex-1 sk:overflow-y-auto sk:p-2',
          quietScrollbar,
        )}
      >
        {entries.map(entry =>
          isGroup(entry) ? (
            <GroupRow
              key={entry.id}
              group={entry}
              activeKey={activeKey}
              maxCount={maxCount}
            />
          ) : (
            <ItemRow
              key={entry.id}
              item={entry}
              isActive={entry.id === activeKey}
              maxCount={maxCount}
              isNested={false}
            />
          ),
        )}
      </ul>
      {footer ? <div className="sk:shrink-0 sk:p-2">{footer}</div> : null}
    </nav>
  );
}
